import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Collapse, Input, Switch, Table, Tabs, Tag, message } from 'antd';
import apiCall from 'apps/catalog/apiCalls/stock-fundamentals';
import InfoCard from 'common/ui/info-card';
import { ChartContainer, type ChartConfig } from 'common/ui/chart';
import { CheckCircleFilled, CloseCircleFilled, MinusCircleOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
} from 'recharts';
import { formatCurrency } from 'utils/formatAmount';
import { formatDate, formatDateTime } from 'utils/formatDate';

// Same 60% rule of thumb as the list page -- flags for a closer look, not a
// verdict that the data is wrong.
const DISCOUNT_PCT_WARN_THRESHOLD = 60;
// How far the DCF estimate can disagree with the blended fair price before
// it's worth calling out -- the pipeline already excludes DCF from the blend
// when this happens (see A32: dcf ~3.5k vs blended ~46.7k), but that
// disagreement itself is a useful "double check this" signal.
const METHOD_DISAGREEMENT_THRESHOLD_PCT = 100;

const scoreColor = (ratio: number) =>
  ratio >= 0.6 ? '#2f9e44' : ratio >= 0.3 ? '#f59f00' : '#e03131';

const AnnualFinancialsTable = ({ annual }: { annual: Record<string, any> }) => {
  const years = Object.keys(annual || {}).sort();
  if (!years.length) return null;

  const metrics: [string, string][] = [
    ['revenue', 'Revenue'],
    ['grossProfit', 'Gross profit'],
    ['pretaxProfit', 'Pretax profit'],
    ['netIncome', 'Net income'],
    ['equity', 'Equity'],
    ['liabilities', 'Liabilities'],
    ['totalAssets', 'Total assets'],
    ['cash', 'Cash'],
    ['operatingCashFlow', 'Operating cash flow'],
    ['capex', 'Capex'],
  ];

  const columns = [
    { title: 'Metric', dataIndex: 'label', key: 'label', fixed: 'left' as const },
    ...years.map(y => ({
      title: y,
      dataIndex: y,
      key: y,
      align: 'right' as const,
      render: (v: number) => formatCurrency(v),
    })),
  ];

  const dataSource = metrics.map(([key, label]) => {
    const row: Record<string, any> = { key, label };
    years.forEach(y => {
      row[y] = annual[y]?.[key] ?? null;
    });
    return row;
  });

  return (
    <Table
      size="small"
      pagination={false}
      scroll={{ x: true }}
      columns={columns}
      dataSource={dataSource}
    />
  );
};

const DCFTable = ({ dcf }: { dcf: any }) => {
  const years = dcf?.years || [];
  if (!years.length) return null;

  const columns = [
    { title: 'Year', dataIndex: 'year', key: 'year' },
    { title: 'Growth %', dataIndex: 'growthPct', key: 'growthPct', align: 'right' as const, render: (v: number) => v?.toFixed?.(1) },
    { title: 'Free cash flow', dataIndex: 'freeCashFlow', key: 'freeCashFlow', align: 'right' as const, render: (v: number) => formatCurrency(v) },
    { title: 'Discounted', dataIndex: 'discounted', key: 'discounted', align: 'right' as const, render: (v: number) => formatCurrency(v) },
  ];

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
        <InfoCard label="DCF fair price" value={formatCurrency(dcf.fairPrice)} />
        <InfoCard label="DCF discount %" value={dcf.discountPct != null ? `${dcf.discountPct.toFixed(1)}%` : '-'} />
        <InfoCard label="Base FCF" value={formatCurrency(dcf.baseFcf)} />
        <InfoCard label="Growth basis" value={dcf.growthBasis || '-'} />
      </div>
      <Table
        size="small"
        pagination={false}
        columns={columns}
        dataSource={years.map((y: any, i: number) => ({ ...y, key: i }))}
      />
    </>
  );
};

// Metric keys that come as fractions (0.0708 = 7.08%) rather than
// already-percent numbers -- verified against real data. Shared between
// peerMedians and valuation.comparisons since both key off the same metric
// names (pe/pb/ps/divYield/roe).
const FRACTION_PCT_METRIC_KEYS = new Set(['divYield', 'roe']);

const formatMetricByKey = (key: string, v: number | null | undefined) => {
  if (v === null || v === undefined) return '-';
  if (FRACTION_PCT_METRIC_KEYS.has(key)) return `${(v * 100).toFixed(2)}%`;
  return v.toFixed(2);
};

const PeersTable = ({ peers, peerMedians }: { peers: any; peerMedians: any }) => {
  const rows = peers?.rows || [];
  if (!rows.length) return null;

  const columns = [
    { title: 'Symbol', dataIndex: 't', key: 't' },
    { title: 'P/E', dataIndex: 'pe', key: 'pe', align: 'right' as const, render: (v: number) => v?.toFixed?.(2) ?? '-' },
    { title: 'P/B', dataIndex: 'pb', key: 'pb', align: 'right' as const, render: (v: number) => v?.toFixed?.(2) ?? '-' },
    { title: 'P/S', dataIndex: 'ps', key: 'ps', align: 'right' as const, render: (v: number) => v?.toFixed?.(2) ?? '-' },
    { title: 'Div yield', dataIndex: 'divYield', key: 'divYield', align: 'right' as const, render: (v: number) => v != null ? `${(v * 100).toFixed(1)}%` : '-' },
    { title: 'Market cap', dataIndex: 'marketCap', key: 'marketCap', align: 'right' as const, render: (v: number) => formatCurrency(v) },
  ];

  return (
    <>
      {peers.label && (
        <p className="text-sm text-gray-500 mb-2">
          Peer group: {peers.label} ({peers.total} symbols, ICB level {peers.level})
        </p>
      )}
      <Table size="small" pagination={false} columns={columns} dataSource={rows.map((r: any, i: number) => ({ ...r, key: i }))} />
      {peerMedians && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-3">
          {Object.entries(peerMedians).map(([k, v]: [string, any]) => (
            <InfoCard key={k} label={`Peer median ${k}`} value={formatMetricByKey(k, v?.value)} />
          ))}
        </div>
      )}
    </>
  );
};

// The source mixes two different conventions for "percent" fields with no
// way to tell them apart by name alone -- verified against a real payload
// (A32, 2026-09-08): fields named "...Pct" (and earningsYield) are already
// in percent form (6.98 means 6.98%), while divYield/roe are fractions
// (0.0708 means 7.08%). Anything not verified against real data is left as
// a plain number rather than guessed, since scaling it wrong the wrong way
// silently misleads whoever is spot-checking this exact data.
type RatioFormat = 'number' | 'pct' | 'fraction-pct' | 'currency';

const formatRatio = (v: any, fmt: RatioFormat) => {
  if (v === null || v === undefined) return '-';
  if (fmt === 'pct') return `${v.toFixed(2)}%`;
  if (fmt === 'fraction-pct') return `${(v * 100).toFixed(2)}%`;
  if (fmt === 'currency') return formatCurrency(v);
  return typeof v === 'number' ? v.toFixed(2) : v;
};

const RatioGroup = ({
  title,
  items,
  data,
}: {
  title: string;
  items: [string, string, RatioFormat][];
  data: Record<string, any>;
}) => {
  const present = items.filter(([key]) => data?.[key] !== undefined && data?.[key] !== null);
  if (!present.length) return null;
  return (
    <div>
      <div className="text-sm font-medium text-gray-600 mb-2">{title}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {present.map(([key, label, fmt]) => (
          <InfoCard key={key} label={label} value={formatRatio(data[key], fmt)} />
        ))}
      </div>
    </div>
  );
};

// payload.estimates{year: {...}} is the company's own AGM plan (VNDirect
// ESTIMATION rows, one year ahead) -- not analyst forecasts. NOT the same as
// payload.valuation.estimates[] (an array of valuation *methods*).
// Unfortunate naming collision in the source.
const ForwardEstimatesTable = ({ estimates }: { estimates: Record<string, any> }) => {
  const years = Object.keys(estimates || {}).sort();
  if (!years.length) return null;

  const metrics: [string, string][] = [
    ['revenue', 'Revenue (plan)'],
    ['pretaxProfit', 'Pretax profit (plan)'],
    ['netIncome', 'Net income (plan)'],
    ['dps', 'DPS (plan)'],
  ];

  const columns = [
    { title: 'Metric', dataIndex: 'label', key: 'label' },
    ...years.map(y => ({
      title: y,
      dataIndex: y,
      key: y,
      align: 'right' as const,
      render: (v: number) => formatCurrency(v),
    })),
  ];

  const dataSource = metrics.map(([key, label]) => {
    const row: Record<string, any> = { key, label };
    years.forEach(y => {
      row[y] = estimates[y]?.[key] ?? null;
    });
    return row;
  });

  return <Table size="small" pagination={false} columns={columns} dataSource={dataSource} />;
};

const DividendsTable = ({ dividends }: { dividends: Record<string, any> }) => {
  const years = Object.keys(dividends || {}).sort();
  if (!years.length) return null;

  return (
    <Table
      size="small"
      pagination={false}
      columns={[
        { title: 'Year', dataIndex: 'year', key: 'year' },
        { title: 'Dividend / share', dataIndex: 'amount', key: 'amount', align: 'right' as const, render: (v: number) => formatCurrency(v) },
      ]}
      dataSource={years.map(y => ({ key: y, year: y, amount: dividends[y] }))}
    />
  );
};

// Multi-series year table -- same "years as columns" layout as
// AnnualFinancialsTable, but generic over which series to show and how to
// format a value, since sections mix currency (revenue/earnings), percent
// (roePct/roaPct) and share-count series. `forecast` points (AGM plan years,
// only present on future.growth series) render in blue with a "(kế hoạch)"
// suffix so a plan year is never mistaken for a reported one.
const SeriesTable = ({
  series,
  formatValue = (v: number) => formatCurrency(v),
}: {
  series: { key: string; label: string; points: { year: number; value: number; forecast?: boolean }[] }[];
  formatValue?: (v: number) => string;
}) => {
  const years = Array.from(new Set(series.flatMap(s => s.points.map(p => p.year)))).sort((a, b) => a - b);
  if (!years.length) return null;

  const columns = [
    { title: 'Metric', dataIndex: 'label', key: 'label', fixed: 'left' as const },
    ...years.map(y => ({
      title: String(y),
      dataIndex: String(y),
      key: String(y),
      align: 'right' as const,
      render: (p: { value: number; forecast?: boolean } | undefined) =>
        p ? (
          <span className={p.forecast ? 'text-blue-600' : ''}>
            {formatValue(p.value)}
            {p.forecast && <span className="text-xs text-gray-400"> (kế hoạch)</span>}
          </span>
        ) : (
          '-'
        ),
    })),
  ];

  const dataSource = series.map(s => {
    const row: Record<string, any> = { key: s.key, label: s.label };
    s.points.forEach(p => {
      row[String(p.year)] = p;
    });
    return row;
  });

  return <Table size="small" pagination={false} scroll={{ x: true }} columns={columns} dataSource={dataSource} />;
};

// One metric of this company against its peer/market reference set --
// mirrors the model's Gauge{metric, value, reference} shape directly, so no
// mapping is needed between what the pipeline emits and what this renders.
const GaugeCard = ({
  label,
  gauge,
  fmt = (v: number) => v.toFixed(2),
}: {
  label: string;
  gauge: any;
  fmt?: (v: number) => string;
}) => {
  if (!gauge) return null;
  const ref = gauge.reference;
  return (
    <InfoCard
      label={label}
      value={
        <div>
          <div>{gauge.value != null ? fmt(gauge.value) : '-'}</div>
          {ref && (ref.peer != null || ref.market != null) && (
            <div className="text-xs font-normal text-gray-500 mt-1">
              {ref.peer != null && `${ref.peerLabel || 'Peer'}: ${fmt(ref.peer)}`}
              {ref.market != null && ` · Market ${fmt(ref.market)}`}
            </div>
          )}
          {/* The model's own caveat (e.g. not scored under this lens) --
          shown to app users as a footnote, so worth seeing here too. */}
          {gauge.note && (
            <div className="text-xs font-normal text-amber-700 mt-1">Note: {gauge.note}</div>
          )}
        </div>
      }
    />
  );
};

// Whether the company delivered its own AGM plan, up to the last 5 years
// with both a plan and a reported result -- the only forecast track record a
// Vietnamese issuer has (see future-growth.ts:planTrackRecord upstream).
const PlanTrackTable = ({ planTrack }: { planTrack: any }) => {
  const years = planTrack?.years || [];
  if (!years.length) return null;

  const hitColor = (actual: number | null, plan: number | null) => {
    if (actual == null || plan == null) return '';
    return actual >= plan ? 'text-green-600' : 'text-red-600';
  };

  const columns = [
    { title: 'Year', dataIndex: 'year', key: 'year' },
    { title: 'Plan revenue', dataIndex: 'planRevenue', key: 'planRevenue', align: 'right' as const, render: (v: number) => formatCurrency(v) },
    {
      title: 'Actual revenue', dataIndex: 'actualRevenue', key: 'actualRevenue', align: 'right' as const,
      render: (v: number, row: any) => <span className={hitColor(v, row.planRevenue)}>{formatCurrency(v)}</span>,
    },
    { title: 'Plan profit', dataIndex: 'planProfit', key: 'planProfit', align: 'right' as const, render: (v: number) => formatCurrency(v) },
    {
      title: 'Actual profit', dataIndex: 'actualProfit', key: 'actualProfit', align: 'right' as const,
      render: (v: number, row: any) => <span className={hitColor(v, row.planProfit)}>{formatCurrency(v)}</span>,
    },
  ];

  return (
    <>
      <p className="text-sm text-gray-500 mb-2">
        Profit plan met {planTrack.profitHits}/{planTrack.profitYears} years · Revenue plan met{' '}
        {planTrack.revenueHits}/{planTrack.revenueYears} years
        {planTrack.medianProfitAchievementPct != null &&
          ` · Median achievement ${planTrack.medianProfitAchievementPct.toFixed(0)}%`}
      </p>
      <Table size="small" pagination={false} columns={columns} dataSource={years.map((y: any, i: number) => ({ ...y, key: i }))} />
    </>
  );
};

const CheckList = ({ checks }: { checks: any[] }) => (
  <ul className="space-y-1">
    {(checks || []).map(c => (
      <li key={c.id} className="flex items-start gap-2 text-sm">
        {c.pass === true && <CheckCircleFilled className="text-green-600 mt-0.5" />}
        {c.pass === false && <CloseCircleFilled className="text-red-500 mt-0.5" />}
        {c.pass == null && <MinusCircleOutlined className="text-gray-400 mt-0.5" />}
        <div>
          <div>{c.label}</div>
          {c.detail && <div className="text-gray-500">{c.detail}</div>}
          {c.requires && <div className="text-gray-400 italic">Requires: {c.requires}</div>}
        </div>
      </li>
    ))}
  </ul>
);

const SnowflakeChart = ({ snowflake }: { snowflake: any }) => {
  if (!snowflake?.axes?.length) return null;

  const ratio = snowflake.possible ? snowflake.total / snowflake.possible : 0;
  const color = scoreColor(ratio);

  const data = snowflake.axes.map((a: any) => ({
    axis: a.short || a.label,
    score: a.score,
  }));

  const config: ChartConfig = { score: { label: 'Score', color } };

  return (
    <div className="flex flex-col md:flex-row gap-6">
      <div className="w-full md:w-80 flex flex-col items-center">
        <ChartContainer config={config} className="h-64 w-full">
          <RadarChart data={data} outerRadius="75%">
            <PolarGrid />
            <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11 }} />
            <PolarRadiusAxis angle={30} domain={[0, snowflake.maxPerAxis || 6]} tick={{ fontSize: 10 }} />
            <Radar dataKey="score" stroke={color} fill={color} fillOpacity={0.35} />
          </RadarChart>
        </ChartContainer>
        <Tag color={ratio >= 0.6 ? 'green' : ratio >= 0.3 ? 'orange' : 'red'} className="text-sm">
          Total score {snowflake.total}/{snowflake.possible}
        </Tag>
      </div>
      <div className="flex-1">
        <Collapse
          size="small"
          items={snowflake.axes.map((a: any) => ({
            key: a.key,
            label: (
              // score is rescaled onto maxPerAxis when some checks can't be
              // evaluated (AAM past: 3 of 4 passed -> score 5), so it's not
              // a count of checks -- scoreRaw/evaluable is.
              <span>
                {a.label}{' '}
                <span className="text-gray-400">
                  score {a.score}/{snowflake.maxPerAxis || 6} · passed {a.scoreRaw ?? '-'}/{a.evaluable} evaluable
                  {a.available === false && ' · not enough data'}
                </span>
              </span>
            ),
            children: <CheckList checks={a.checks} />,
          }))}
        />
      </div>
    </div>
  );
};

// Horizontal position of `price` within [low, high], clamped to [0, 100] with
// a little headroom so a price outside the range is still visible at an edge.
const ValuationGauge = ({ price, low, high }: { price: number; low: number; high: number }) => {
  if (low == null || high == null || price == null || high <= low) return null;

  const pricePct = Math.min(100, Math.max(0, ((price - low) / (high - low)) * 100));

  return (
    <div className="max-w-xl">
      <div className="relative h-3 rounded-full bg-gradient-to-r from-green-500 via-gray-300 to-red-500">
        <div
          className="absolute -top-1.5 w-1 h-6 bg-black rounded"
          style={{ left: `calc(${pricePct}% - 2px)` }}
          title={`Price: ${formatCurrency(price)}`}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>Low {formatCurrency(low)}</span>
        <span>High {formatCurrency(high)}</span>
      </div>
    </div>
  );
};

const ItemDetail = ({ item }) => {
  const {
    id,
    symbol,
    stock_id,
    generated_at,
    priced_at,
    excluded_from_app,
    excluded_reason,
    payload,
  } = item || {};

  const queryClient = useQueryClient();
  const [excluded, setExcluded] = useState(!!excluded_from_app);
  const [reason, setReason] = useState(excluded_reason || '');

  useEffect(() => {
    setExcluded(!!excluded_from_app);
    setReason(excluded_reason || '');
  }, [id, excluded_from_app, excluded_reason]);

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: { excluded_from_app: boolean; excluded_reason: string }) =>
      apiCall.edit.queryFn({ id, payload }),
    onSuccess: () => {
      message.success('Saved');
      queryClient.invalidateQueries({ queryKey: [apiCall.detail.queryKey] });
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to save');
    },
  });

  const dirty =
    excluded !== !!excluded_from_app || reason !== (excluded_reason || '');

  const parsed = useMemo(() => {
    try {
      return JSON.parse(payload);
    } catch {
      return null;
    }
  }, [payload]);

  const prettyPayload = useMemo(() => {
    if (!parsed) return payload;
    return JSON.stringify(parsed, null, 2);
  }, [parsed, payload]);

  const valuation = parsed?.valuation;
  const sections = parsed?.valuation?.sections;

  const methodDisagreement = useMemo(() => {
    if (!valuation) return null;
    const candidates: number[] = [valuation.fairPrice, valuation.dcf?.fairPrice].filter(
      (v): v is number => typeof v === 'number' && v > 0
    );
    if (candidates.length < 2) return null;
    const max = Math.max(...candidates);
    const min = Math.min(...candidates);
    const spreadPct = ((max - min) / min) * 100;
    return spreadPct >= METHOD_DISAGREEMENT_THRESHOLD_PCT ? spreadPct : null;
  }, [valuation]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <InfoCard label="Symbol" value={symbol} />
        <InfoCard label="Price" value={formatCurrency(parsed?.price)} />
        <InfoCard label="Exchange" value={parsed?.exchange || '-'} />
        <InfoCard label="Fiscal year" value={parsed?.fiscalYear || '-'} />
        <InfoCard label="Company form" value={parsed?.form || '-'} />
      </div>
      {parsed?.icb && (
        <div className="flex flex-wrap gap-2">
          {[parsed.icb.l1, parsed.icb.l2, parsed.icb.l3, parsed.icb.l4].filter(Boolean).map((v: string, i: number) => (
            <Tag key={i}>{v}</Tag>
          ))}
          {/* Conglomerates get no industry peers and no DCF (see the model's
          conglomerates.ts) -- the usual reason those are missing. */}
          {parsed.conglomerate && <Tag color="purple">Conglomerate: {parsed.conglomerate}</Tag>}
        </div>
      )}
      <div className="text-xs text-gray-400">
        Priced at {formatDate(priced_at)} · Synced at {formatDateTime(generated_at)} · Stock ID {stock_id}
      </div>

      {methodDisagreement != null && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
          Valuation methods disagree by {methodDisagreement.toFixed(0)}% (DCF fair price{' '}
          {formatCurrency(valuation.dcf?.fairPrice)} vs blended fair price {formatCurrency(valuation.fairPrice)}).
          Worth a closer look before trusting this symbol's valuation.
        </div>
      )}

      <div className="bg-gray-50 border border-border rounded p-4">
        <div className="text-sm font-medium text-gray-800 mb-2">Admin override</div>
        <div className="flex flex-col gap-3 max-w-xl">
          <div className="flex items-center gap-3">
            <Switch checked={excluded} onChange={setExcluded} />
            <span>Hide this symbol from the app</span>
          </div>
          {excluded && (
            <Input.TextArea
              rows={2}
              placeholder="Why is this excluded? (visible to other admins only)"
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
          )}
          <div>
            <Button
              type="primary"
              disabled={!dirty}
              loading={isPending}
              onClick={() =>
                mutate({ excluded_from_app: excluded, excluded_reason: reason })
              }
            >
              Save
            </Button>
          </div>
        </div>
      </div>

      <Tabs
        items={[
          {
            key: 'ratios',
            label: 'Ratios',
            children: (
              <div className="space-y-3">
                <RatioGroup
                  title="Valuation multiples"
                  data={parsed}
                  items={[
                    ['pe', 'P/E', 'number'],
                    ['pb', 'P/B', 'number'],
                    ['ps', 'P/S', 'number'],
                    ['eps', 'EPS', 'currency'],
                    ['bvps', 'BVPS', 'currency'],
                    ['marketCap', 'Market cap', 'currency'],
                    ['pe5y', 'P/E (5y avg)', 'number'],
                    ['pb5y', 'P/B (5y avg)', 'number'],
                  ]}
                />
                <RatioGroup
                  title="Profitability"
                  data={parsed}
                  items={[
                    ['roe', 'ROE', 'fraction-pct'],
                    ['divYield', 'Dividend yield', 'fraction-pct'],
                    ['earningsYield', 'Earnings yield', 'pct'],
                    ['netMarginPct', 'Net margin', 'pct'],
                    ['grossMarginPct', 'Gross margin', 'pct'],
                    ['ebitMargin', 'EBIT margin', 'fraction-pct'],
                    ['roaa', 'ROAA', 'fraction-pct'],
                    ['payout', 'Payout ratio', 'fraction-pct'],
                  ]}
                />
                <RatioGroup
                  title="Growth"
                  data={parsed}
                  items={[
                    ['revenueGrowthPct', 'Revenue growth', 'pct'],
                    ['pretaxGrowthPct', 'Pretax profit growth', 'pct'],
                    ['revenueCagrPct', 'Revenue CAGR', 'pct'],
                    ['pretaxCagrPct', 'Pretax CAGR', 'pct'],
                    ['netCagrPct', 'Net income CAGR', 'pct'],
                    ['marginDeltaPct', 'Margin change', 'pct'],
                  ]}
                />
                <RatioGroup
                  title="Financial health"
                  data={parsed}
                  items={[
                    ['debtEquity', 'Debt / Equity', 'number'],
                    ['currentRatio', 'Current ratio', 'number'],
                    ['interestCover', 'Interest cover', 'number'],
                    ['feShareOfEbitPct', 'Financial expense / EBIT', 'pct'],
                    ['npl', 'NPL ratio (bank)', 'fraction-pct'],
                    ['car', 'CAR (bank)', 'fraction-pct'],
                  ]}
                />
              </div>
            ),
          },
          {
            key: 'valuation',
            label: 'Valuation',
            children: valuation?.available === false ? (
              // Unpriced/suspended tickers and others the model wouldn't
              // value: the payload is just {available: false, note}, so the
              // note is the only thing worth showing.
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700">
                Not valued by the model{valuation.note ? `: ${valuation.note}` : '.'}
              </div>
            ) : valuation ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <InfoCard label="Fair price" value={formatCurrency(valuation.fairPrice)} />
                  <InfoCard
                    label="Discount %"
                    value={
                      valuation.discountPct != null ? (
                        <span className={Math.abs(valuation.discountPct) >= DISCOUNT_PCT_WARN_THRESHOLD ? 'text-red-600 font-medium' : ''}>
                          {valuation.discountPct.toFixed(1)}%
                        </span>
                      ) : '-'
                    }
                  />
                  <InfoCard label="Snowflake" value={valuation.snowflake ? `${valuation.snowflake.total}/${valuation.snowflake.possible}` : '-'} />
                  <InfoCard label="Peer count" value={valuation.snowflake?.peerCount ?? '-'} />
                </div>
                <ValuationGauge price={parsed?.price} low={valuation.fairLow} high={valuation.fairHigh} />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <InfoCard label="Forward P/E" value={valuation.forwardPe?.toFixed?.(2) ?? '-'} />
                  <InfoCard label="Forward growth %" value={valuation.forwardGrowthPct != null ? `${valuation.forwardGrowthPct.toFixed(1)}%` : '-'} />
                  <InfoCard label="Forward year" value={valuation.forwardYear ?? '-'} />
                  <InfoCard label="PEG" value={valuation.peg?.toFixed?.(2) ?? '-'} />
                </div>

                {valuation.estimates?.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {valuation.estimates.map((e: any) => (
                      <InfoCard
                        key={e.key}
                        label={e.label}
                        value={
                          <div>
                            <div>{formatCurrency(e.fairPrice)}</div>
                            {e.basis && <div className="text-xs font-normal text-gray-500 mt-1">{e.basis}</div>}
                          </div>
                        }
                      />
                    ))}
                  </div>
                )}

                {valuation.snowflake && (
                  <>
                    <div className="text-sm font-medium text-gray-600">Snowflake</div>
                    <SnowflakeChart snowflake={valuation.snowflake} />
                  </>
                )}

                {valuation.dcf?.available === false ? (
                  // The app shows this same note in place of the intrinsic
                  // value (backend dcf_note).
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Intrinsic value: not computed.</span> {valuation.dcf.note}
                  </div>
                ) : (
                  valuation.dcf && (
                    <>
                      <div className="text-sm font-medium text-gray-600">
                        Intrinsic value · {valuation.dcf.methodLabel || valuation.dcf.method}
                        {valuation.dcf.years?.length > 0 && ` (${valuation.dcf.years.length}-year projection)`}
                      </div>
                      {valuation.dcf.years?.length > 0 ? (
                        <DCFTable dcf={valuation.dcf} />
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <InfoCard label="Fair price" value={formatCurrency(valuation.dcf.fairPrice)} />
                          <InfoCard label="Discount %" value={valuation.dcf.discountPct != null ? `${valuation.dcf.discountPct.toFixed(1)}%` : '-'} />
                          <InfoCard label="Book value / share" value={formatCurrency(valuation.dcf.bookValue)} />
                          <InfoCard label="ROE" value={valuation.dcf.roePct != null ? `${valuation.dcf.roePct.toFixed(1)}%` : '-'} />
                        </div>
                      )}
                    </>
                  )
                )}

                {valuation.comparisons?.length > 0 && (
                  <>
                    <div className="text-sm font-medium text-gray-600">Peer / market comparisons</div>
                    <Table
                      size="small"
                      pagination={false}
                      rowKey="_rowKey"
                      columns={[
                        { title: 'Metric', dataIndex: 'key', key: 'metric' },
                        { title: 'Value', dataIndex: 'value', key: 'value', align: 'right' as const, render: (v: number, r: any) => formatMetricByKey(r.key, v) },
                        { title: 'Peer median', dataIndex: 'peerMedian', key: 'peerMedian', align: 'right' as const, render: (v: number, r: any) => formatMetricByKey(r.key, v) },
                        { title: 'Market median', dataIndex: 'marketMedian', key: 'marketMedian', align: 'right' as const, render: (v: number, r: any) => formatMetricByKey(r.key, v) },
                        { title: 'vs Peer %', dataIndex: 'vsPeerPct', key: 'vsPeerPct', align: 'right' as const, render: (v: number) => v != null ? `${v.toFixed(1)}%` : '-' },
                      ]}
                      dataSource={valuation.comparisons.map((c: any, i: number) => ({ ...c, _rowKey: i }))}
                    />
                  </>
                )}
              </div>
            ) : (
              <span className="text-gray-400">No valuation available for this symbol.</span>
            ),
          },
          {
            key: 'sections',
            label: 'Sections',
            children: sections ? (
              <div className="space-y-6">
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-2">Future Growth</div>
                  {sections.future?.scopeWarning && (
                    <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700 mb-3">
                      {sections.future.scopeWarning}
                    </div>
                  )}
                  <SeriesTable
                    series={[
                      { key: 'revenue', label: 'Revenue', points: sections.future?.growth?.revenue || [] },
                      { key: 'earnings', label: 'Net income', points: sections.future?.growth?.earnings || [] },
                      { key: 'eps', label: 'EPS', points: sections.future?.growth?.eps || [] },
                    ]}
                  />
                  {sections.future?.planTrack?.years?.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs font-medium text-gray-500 mb-1">AGM plan track record</div>
                      <PlanTrackTable planTrack={sections.future.planTrack} />
                    </div>
                  )}
                  {sections.future?.planChecks?.length > 0 && (
                    <div className="mt-3">
                      <CheckList checks={sections.future.planChecks} />
                    </div>
                  )}
                  {sections.future?.futureRoe && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                      <InfoCard label="Future ROE method" value={sections.future.futureRoe.method || '-'} />
                      <InfoCard
                        label={`Future ROE (${sections.future.futureRoe.horizonYears}y)`}
                        value={`${sections.future.futureRoe.pct?.toFixed?.(1)}%`}
                      />
                      <GaugeCard
                        label="Future ROE vs peer/market"
                        gauge={sections.future.futureRoe.gauge}
                        fmt={(v: number) => `${(v * 100).toFixed(1)}%`}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-600 mb-2">Past Performance</div>
                  <SeriesTable
                    series={[
                      { key: 'earnings', label: 'Net income', points: sections.past?.earnings || [] },
                      { key: 'ocf', label: 'Operating cash flow', points: sections.past?.operatingCashFlow || [] },
                      { key: 'roe', label: 'ROE', points: sections.past?.roePct || [] },
                      { key: 'roa', label: 'ROA', points: sections.past?.roaPct || [] },
                    ]}
                    formatValue={(v: number) => (Math.abs(v) < 1000 ? `${v.toFixed(2)}%` : formatCurrency(v))}
                  />
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-3">
                    <GaugeCard label="Net income CAGR" gauge={sections.past?.gauges?.netCagrPct} fmt={(v: number) => `${v.toFixed(1)}%`} />
                    <GaugeCard label="Revenue CAGR" gauge={sections.past?.gauges?.revenueCagrPct} fmt={(v: number) => `${v.toFixed(1)}%`} />
                    <GaugeCard label="ROE" gauge={sections.past?.gauges?.roe} fmt={(v: number) => `${(v * 100).toFixed(1)}%`} />
                    <GaugeCard label="ROAA" gauge={sections.past?.gauges?.roaa} fmt={(v: number) => `${(v * 100).toFixed(1)}%`} />
                    <GaugeCard label="Net margin" gauge={sections.past?.gauges?.netMarginPct} fmt={(v: number) => `${v.toFixed(1)}%`} />
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-600 mb-2">
                    Financial Health <span className="text-gray-400 font-normal">(lens: {sections.health?.lens || 'default'})</span>
                  </div>
                  {sections.health?.lensChecks?.length > 0 && (
                    <div className="mb-3">
                      <CheckList checks={sections.health.lensChecks} />
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                    {sections.health?.shortTerm && (
                      <InfoCard
                        label="Short-term assets / liabilities"
                        value={`${formatCurrency(sections.health.shortTerm.assets)} / ${formatCurrency(sections.health.shortTerm.liabilities)}`}
                      />
                    )}
                    {sections.health?.longTerm && (
                      <InfoCard
                        label="Long-term assets / liabilities"
                        value={`${formatCurrency(sections.health.longTerm.assets)} / ${formatCurrency(sections.health.longTerm.liabilities)}`}
                      />
                    )}
                  </div>
                  <SeriesTable
                    series={[
                      { key: 'liabilities', label: 'Liabilities', points: sections.health?.liabilities || [] },
                      { key: 'borrowings', label: 'Borrowings', points: sections.health?.borrowings || [] },
                      { key: 'equity', label: 'Equity', points: sections.health?.equity || [] },
                      { key: 'cash', label: 'Cash', points: sections.health?.cash || [] },
                    ]}
                  />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                    <GaugeCard label="Net debt / equity" gauge={sections.health?.gauges?.netDebtEquity} />
                    <GaugeCard label="Debt / equity" gauge={sections.health?.gauges?.debtEquity} />
                    <GaugeCard label="Current ratio" gauge={sections.health?.gauges?.currentRatio} />
                    <GaugeCard label="Interest cover" gauge={sections.health?.gauges?.interestCover} />
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-600 mb-2">Dividend</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                    <InfoCard label="Avg DPS" value={formatCurrency(sections.dividend?.avgDps)} />
                    <InfoCard label="Paid years" value={sections.dividend?.paidYears ?? '-'} />
                    <InfoCard
                      label="Cash yield"
                      value={sections.dividend?.cashYieldPct != null ? `${sections.dividend.cashYieldPct.toFixed(2)}%` : '-'}
                    />
                    <InfoCard
                      label="Payout of earnings"
                      value={sections.dividend?.payoutOfEarningsPct != null ? `${sections.dividend.payoutOfEarningsPct.toFixed(1)}%` : '-'}
                    />
                    {/* -1 is the model's sentinel for "free cash flow itself
                    is negative", not a -1% payout. */}
                    <InfoCard
                      label="Payout of FCF"
                      value={
                        sections.dividend?.payoutOfFcfPct == null
                          ? '-'
                          : sections.dividend.payoutOfFcfPct === -1
                            ? 'FCF negative'
                            : `${sections.dividend.payoutOfFcfPct.toFixed(1)}%`
                      }
                    />
                    <InfoCard
                      label="Scoring window"
                      value={
                        sections.dividend?.window
                          ? `${sections.dividend.window.fromYear}–${sections.dividend.window.toYear}`
                          : '-'
                      }
                    />
                  </div>
                  <SeriesTable series={[{ key: 'dps', label: 'Cash DPS', points: sections.dividend?.cashHistory || [] }]} />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                    <GaugeCard label="Cash yield vs peer/market" gauge={sections.dividend?.yieldGauge} fmt={(v: number) => `${v.toFixed(2)}%`} />
                    {sections.dividend?.shareGrowth && (
                      <InfoCard
                        label={`Share count growth (${sections.dividend.shareGrowth.fromYear}→${sections.dividend.shareGrowth.toYear})`}
                        value={`${sections.dividend.shareGrowth.pct.toFixed(0)}%`}
                      />
                    )}
                  </div>
                  {sections.dividend?.stockDividends?.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs font-medium text-gray-500 mb-1">Stock dividends (shares per 100 held)</div>
                      <Table
                        size="small"
                        pagination={false}
                        columns={[
                          { title: 'Year', dataIndex: 'year', key: 'year' },
                          { title: 'Ratio', dataIndex: 'ratio', key: 'ratio', align: 'right' as const },
                        ]}
                        dataSource={sections.dividend.stockDividends.map((d: any, i: number) => ({ ...d, key: i }))}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <span className="text-gray-400">No sections data synced for this symbol yet.</span>
            ),
          },
          {
            key: 'financials',
            label: 'Financials (BCTC)',
            children: (
              <div className="space-y-4">
                {parsed?.annual && (
                  <>
                    <div className="text-sm font-medium text-gray-600">Annual</div>
                    <AnnualFinancialsTable annual={parsed.annual} />
                  </>
                )}
                {parsed?.estimates && (
                  <>
                    <div className="text-sm font-medium text-gray-600">AGM plan (kế hoạch ĐHCĐ, by fiscal year)</div>
                    <ForwardEstimatesTable estimates={parsed.estimates} />
                  </>
                )}
                {parsed?.dividends && (
                  <>
                    <div className="text-sm font-medium text-gray-600">Dividends (per share, by year)</div>
                    <DividendsTable dividends={parsed.dividends} />
                  </>
                )}
              </div>
            ),
          },
          {
            key: 'peers',
            label: 'Peers',
            children: parsed?.peers ? (
              <PeersTable peers={parsed.peers} peerMedians={parsed.peerMedians} />
            ) : (
              <span className="text-gray-400">No peer group data for this symbol.</span>
            ),
          },
        ]}
      />

      <Collapse
        size="small"
        items={[
          {
            key: 'raw',
            label: 'Raw synced payload',
            children: (
              <pre className="bg-gray-50 rounded p-4 text-xs overflow-auto max-h-[600px]">
                {prettyPayload}
              </pre>
            ),
          },
        ]}
      />
    </div>
  );
};

export default ItemDetail;
